import React, { useState, useEffect, useRef } from 'react';
import { contentService } from '../services/contentService.ts';

interface EditableContentProps {
  contentId: string;
  contentType: 'text' | 'image';
  defaultValue: string;
  isEditing: boolean;
  onSave: (contentId: string, value: string, contentType: 'text' | 'image') => void;
  onDirtyStateChange?: (contentId: string, isDirty: boolean) => void;
  className?: string;
  tag?: 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span' | 'li' | 'dt' | 'dd' | 'small' | 'strong' | 'em';
  hideSaveButton?: boolean;
  onValueChange?: (contentId: string, value: string) => void;
  placeholder?: string;
  // Force a textarea in edit mode regardless of tag (for multi-line values)
  multiline?: boolean;
}

const EditableContent: React.FC<EditableContentProps> = ({
  contentId,
  contentType,
  defaultValue,
  isEditing,
  onSave,
  onDirtyStateChange,
  className = '',
  tag = 'p',
  hideSaveButton = false,
  onValueChange,
  placeholder = '',
  multiline = false
}) => {
  const [value, setValue] = useState(defaultValue);
  const [lastSavedValue, setLastSavedValue] = useState(defaultValue);
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(defaultValue);
    setLastSavedValue(defaultValue);
    setIsDirty(false);
  }, [defaultValue]);

  // Notify parent of value changes
  useEffect(() => {
    if (onValueChange) {
      onValueChange(contentId, value);
    }
  }, [value, contentId, onValueChange]);

  // Track dirty state changes and notify parent
  useEffect(() => {
    const dirty = value !== lastSavedValue;
    setIsDirty(dirty);
    if (onDirtyStateChange) {
      onDirtyStateChange(contentId, dirty);
    }
  }, [value, lastSavedValue, contentId, onDirtyStateChange]);

  const handleSave = async () => {
    if (!isDirty) return; // Don't save if not dirty

    setIsLoading(true);
    try {
      await onSave(contentId, value, contentType);
      // Update lastSavedValue after successful save
      setLastSavedValue(value);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error saving content:', error);
      }
      // Revert to last saved value on error
      setValue(lastSavedValue);
      alert('保存失敗，請重試');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('請選擇圖片文件');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('圖片大小不能超過 5MB');
      return;
    }

    setIsLoading(true);
    setUploadProgress('上傳中...');

    try {
      // Upload image to server
      const imageUrl = await contentService.uploadImage(file);

      setUploadProgress('保存中...');

      // Save the image URL to database
      await onSave(contentId, imageUrl, 'image');
      setValue(imageUrl);

      setUploadProgress('保存成功！');
      setTimeout(() => setUploadProgress(''), 2000);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error uploading image:', error);
      }
      alert('圖片上傳失敗，請重試');
      setUploadProgress('');
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRevert = () => {
    setValue(lastSavedValue);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      handleRevert();
    }
  };

  if (contentType === 'image') {
    return (
      <div className={`editable-image-container ${className || ''}`}>
        <img src={value} alt="Editable content" className={className || 'editable-image'} />
        {isEditing && (
          <div className="image-edit-overlay">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="image-file-input"
              disabled={isLoading}
              id={`file-input-${contentId}`}
            />
            <label htmlFor={`file-input-${contentId}`} className="upload-button">
              {isLoading ? uploadProgress : '上傳圖片'}
            </label>
          </div>
        )}
      </div>
    );
  }

  // Text content
  const Tag = tag as keyof JSX.IntrinsicElements;

  if (isEditing) {
    if (tag === 'p' || multiline) {
      return (
        <div className="editable-text-wrapper">
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyPress}
            className={`editable-textarea ${className} ${isDirty ? 'editable-dirty' : ''}`}
            disabled={isLoading}
            rows={3}
            placeholder={placeholder}
          />
          {!hideSaveButton && (
            <div className="save-button-container">
              {isDirty && <span className="unsaved-warning">⚠️</span>}
              <button
                className="save-button"
                onClick={handleSave}
                disabled={!isDirty || isLoading}
                type="button"
              >
                {isLoading ? '保存中...' : '保存'}
              </button>
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div className="editable-text-wrapper">
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyPress}
            className={`editable-input ${className} ${isDirty ? 'editable-dirty' : ''}`}
            disabled={isLoading}
            placeholder={placeholder}
          />
          {!hideSaveButton && (
            <div className="save-button-container">
              {isDirty && <span className="unsaved-warning">⚠️</span>}
              <button
                className="save-button"
                onClick={handleSave}
                disabled={!isDirty || isLoading}
                type="button"
              >
                {isLoading ? '保存中...' : '保存'}
              </button>
            </div>
          )}
        </div>
      );
    }
  }

  return (
    <Tag className={`editable-content ${className} ${isEditing ? 'editing' : ''}`}>
      {value}
    </Tag>
  );
};

export default EditableContent;